package com.htmlagent.site.state

import arrow.core.Either
import arrow.core.left
import arrow.core.right
import com.htmlagent.shared.auth.MeResponse
import com.htmlagent.site.api.AgentClient
import com.htmlagent.site.api.ApiFailure
import kotlinx.browser.document
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.withTimeoutOrNull
import org.w3c.dom.events.Event

/** How often the waiting window asks, while nothing wakes it sooner. */
private const val POLL_MILLIS = 3_000L

/**
 * Waits until the link asked for from this browser signs it in: approved from another browser, or
 * opened in another tab of this one. Answers who it is, or why there is nothing left to wait for.
 *
 * It asks every few seconds, and at once when the window comes back to the front: that is the
 * moment the person returns from the browser where they approved it. A dropped connection is not
 * a reason to stop waiting. Cancelling the caller's coroutine is how it stops.
 */
suspend fun AgentClient.awaitSignIn(): Either<ApiFailure, MeResponse> {
    val woken = Channel<Unit>(Channel.CONFLATED)
    val onVisible: (Event) -> Unit = {
        if (document.asDynamic().visibilityState == "visible") woken.trySend(Unit)
    }
    document.addEventListener("visibilitychange", onVisible)
    try {
        while (true) {
            askOnce()?.let { return it }
            withTimeoutOrNull(POLL_MILLIS) { woken.receive() }
        }
    } finally {
        document.removeEventListener("visibilitychange", onVisible)
    }
}

/** One question: who it is, why to stop waiting, or `null` to ask again later. */
private suspend fun AgentClient.askOnce(): Either<ApiFailure, MeResponse>? =
    when (val asked = awaitApproval()) {
        is Either.Right -> asked.value.me?.right()
        // Opened in another tab of this browser, the link signed in there and is spent.
        is Either.Left -> me().getOrNull()?.right() ?: asked.value.takeUnless { it is ApiFailure.Unreachable }?.left()
    }
